import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import RLShopTensionPresetCard from "@/modules/racket_lab_admin/RLShopTensionPresetCard";
import RLShopLowStockThresholdCard from "@/modules/racket_lab_admin/RLShopLowStockThresholdCard";
import RLShopSettingsRequestCard from "@/modules/racket_lab_admin/RLShopSettingsRequestCard";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminShopDetail — Sub-sprint A read-only shop detail.
 *
 * v0 surface: identification fields only. Future sub-sprints will add
 * machines, capabilities, operators, shifts panels — but those are
 * Sub-sprint B/C work.
 *
 * QUICK 1.C i18n migration: back link, headers, loading/error states
 * and Field labels resolved via `racketLab.shops.detail.*` and
 * `racketLab.common.labels.*`. `data-testid`s and routing unchanged.
 */
const Field = ({ label, value, testId }) => (
  <div data-testid={testId} className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">
      {label}
    </span>
    <span className="text-sm text-slate-800 break-all">{value ?? "—"}</span>
  </div>
);

const RLAdminShopDetail = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading, error, reload } = useAdminApi(
    `/admin/racket-lab/shops/${encodeURIComponent(id)}`,
    { skip: !id },
  );

  return (
    <div
      data-testid="rl-admin-shop-detail-page"
      className="flex flex-col gap-4"
    >
      <PilotBanner />
      <RLNavSidebar />

      <button
        type="button"
        onClick={() => navigate("/admin/racket-lab/workforce/shops")}
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
        data-testid="rl-shop-detail-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("shops.detail.back")}
      </button>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          {t("shops.detail.title")}
        </h1>
        <p className="text-xs text-slate-500">
          {t("shops.detail.subtitle")}
        </p>
      </header>

      {loading && (
        <div
          data-testid="rl-shop-detail-loading"
          className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500"
        >
          {t("shops.detail.loading", { id: id?.slice(0, 12) ?? "" })}
        </div>
      )}

      {!loading && error && (
        <div
          data-testid="rl-shop-detail-error"
          className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
        >
          {error.status === 404
            ? t("shops.detail.not_found", { id: id?.slice(0, 12) ?? "" })
            : `${t("dashboard.error_prefix")} ${error.status || ""}: ${error.detail || t("dashboard.error_unknown")}`}
        </div>
      )}

      {!loading && !error && data && (
        <div
          data-testid="rl-shop-detail-card"
          className="grid grid-cols-1 gap-4 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-2"
        >
          <Field label={t("shops.columns.name")} value={data.name} testId="rl-shop-field-name" />
          <Field label={t("shops.columns.country")} value={data.country} testId="rl-shop-field-country" />
          <Field
            label={t("shops.columns.organisation")}
            value={data.organisation_id}
            testId="rl-shop-field-organisation"
          />
          <Field label={t("shops.columns.shop_id")} value={data.id} testId="rl-shop-field-id" />
          <Field label={t("shops.detail.created_at")} value={data.created_at} testId="rl-shop-field-created-at" />
          <Field
            label={t("shops.detail.status")}
            value={data.status || "active"}
            testId="rl-shop-field-status"
          />
        </div>
      )}

      {!loading && !error && data && (
        <RLShopTensionPresetCard key={data.id} shop={data} onSaved={() => reload?.()} />
      )}

      {!loading && !error && data && (
        <RLShopLowStockThresholdCard key={`lst-${data.id}`} shop={data} onSaved={() => reload?.()} />
      )}

      {!loading && !error && data && (
        <RLShopSettingsRequestCard key={`req-${data.id}`} shop={data} onShopReload={() => reload?.()} />
      )}
    </div>
  );
};

export default RLAdminShopDetail;
